import { Empty, Pagination, Spin, Tag } from '@/ui/compat/antd';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import AmicaleReservationActions from './AmicaleReservationActions';
import { AMICALE_VALIDATION_TAB } from './AmicaleValidationProductTabs';

const VOUCHER_STATUS_KEYS = {
  disponible: 'amicales.filters.voucherAvailable',
  'sur demande': 'amicales.filters.voucherOnRequest',
  annulé: 'amicales.filters.voucherCancelled',
  cloturé: 'amicales.filters.voucherClosed',
};

const VOUCHER_STATUS_STYLES = {
  disponible: 'bg-green-100 text-green-800',
  'sur demande': 'bg-yellow-100 text-yellow-800',
  cloturé: 'bg-blue-100 text-blue-800',
  annulé: 'bg-red-100 text-red-800',
};

const VoucherStatusBadge = ({ voucherStatus }) => {
  const { t } = useTranslation();
  const status = voucherStatus || 'disponible';
  const labelKey = VOUCHER_STATUS_KEYS[status];
  const label = labelKey ? t(labelKey) : status;
  const style = VOUCHER_STATUS_STYLES[status] || 'bg-gray-100 text-gray-600';

  return (
    <span
      className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold leading-5 ${style}`}
    >
      {label}
    </span>
  );
};

const formatDate = (d, withTime = false) =>
  d ? dayjs(d).format(`DD/MM/YYYY${withTime ? ' HH:mm' : ''}`) : '-';

const formatAmount = (n, currency = 'TND') =>
  Number.isFinite(Number(n))
    ? `${Number(n).toLocaleString('fr-FR', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })} ${currency}`
    : '-';

const getFactureKey = (row, productType) => {
  if (productType === AMICALE_VALIDATION_TAB.HOTEL) return `hotel:${row.id}`;
  return `ca:${row.id}`;
};

const AmicaleReservationsTable = ({
  rows = [],
  total = 0,
  loading = false,
  page = 1,
  pageSize = 25,
  onPageChange,
  voucherEmail,
  amicalClient,
  onRefresh,
  factureByReservation = {},
  clientId,
  balance,
  productType = AMICALE_VALIDATION_TAB.HOTEL,
}) => {
  const { t } = useTranslation();
  const isHotel = productType === AMICALE_VALIDATION_TAB.HOTEL;

  if (loading) {
    return (
      <div className='bg-white rounded-2xl border border-gray-100 p-10 flex justify-center'>
        <Spin size='large' />
      </div>
    );
  }

  if (!rows.length) {
    return (
      <div className='bg-white rounded-2xl border border-gray-100 p-10'>
        <Empty description={t('amicales.table.empty')} />
      </div>
    );
  }

  return (
    <div className='bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden'>
      <div className='overflow-x-auto'>
        <table className='min-w-full divide-y divide-gray-200 text-sm'>
          <thead className='bg-gray-50 text-gray-600 uppercase text-xs'>
            <tr>
              <th className='px-4 py-3 text-left font-semibold'>{t('amicales.table.reference')}</th>
              <th className='px-4 py-3 text-left font-semibold'>
                {isHotel ? t('amicales.table.hotel') : t('amicales.table.product')}
              </th>
              <th className='px-4 py-3 text-left font-semibold'>{t('amicales.table.client')}</th>
              <th className='px-4 py-3 text-left font-semibold'>
                {isHotel ? t('amicales.table.stay') : t('amicales.table.access')}
              </th>
              <th className='px-4 py-3 text-right font-semibold'>{t('amicales.table.amount')}</th>
              <th className='px-4 py-3 text-center font-semibold'>{t('amicales.table.status')}</th>
              <th className='px-4 py-3 text-right font-semibold'>{t('amicales.table.actions')}</th>
            </tr>
          </thead>
          <tbody className='divide-y divide-gray-100'>
            {rows.map((r) => {
              const voucherOnRequest = r.voucher_status === 'sur demande';
              const matricule = r.matricule_amical || r.matricule_fiscal_amical;
              const factureKey = getFactureKey(r, productType);
              const factureMap = { [factureKey]: factureByReservation[factureKey] };

              return (
                <tr key={`${productType}-${r.id}`} className='hover:bg-gray-50'>
                  <td className='px-4 py-3 text-gray-800 font-mono'>
                    {isHotel && voucherOnRequest ? (
                      <Tag color='gold' className='font-sans font-semibold'>
                        {t('amicales.table.onRequest')}
                      </Tag>
                    ) : (
                      r.id_booking || r.ref || `#${r.id}`
                    )}
                    <div className='text-xs text-gray-400'>
                      {formatDate(r.booking_date, true)}
                    </div>
                  </td>
                  <td className='px-4 py-3'>
                    {isHotel ? (
                      <>
                        <div className='font-semibold text-gray-800'>
                          {r.hotel_name || '-'}
                        </div>
                        <div className='text-xs text-gray-500'>
                          {[r.ville_name, r.pays_name].filter(Boolean).join(', ')}
                        </div>
                      </>
                    ) : (
                      <>
                        <div className='font-semibold text-gray-800'>
                          {r.carte_label || '-'}
                        </div>
                        <div className='text-xs text-gray-500'>
                          {r.facility_label || '-'}
                        </div>
                      </>
                    )}
                  </td>
                  <td className='px-4 py-3'>
                    <div className='font-semibold text-gray-800'>
                      {r.client_name || '—'}
                    </div>
                    {r.civilite_amical || matricule || r.telephone_amical ? (
                      <div className='mt-1 space-y-0.5 text-xs text-gray-500'>
                        {r.civilite_amical ? <div>{r.civilite_amical}</div> : null}
                        {matricule ? (
                          <div>
                            {t('amicales.table.matriculePrefix')}{' '}
                            <span className='font-mono font-semibold text-gray-700'>
                              {matricule}
                            </span>
                          </div>
                        ) : null}
                        {r.telephone_amical ? (
                          <div>
                            {t('amicales.table.phonePrefix')} {r.telephone_amical}
                          </div>
                        ) : null}
                      </div>
                    ) : null}
                  </td>
                  <td className='px-4 py-3 text-gray-700'>
                    {isHotel ? (
                      <>
                        {formatDate(r.check_in)} → {formatDate(r.check_out)}
                        <div className='text-xs text-gray-500'>
                          {(r.adults || 0)} {t('amicales.table.adultsShort')} · {(r.children || 0)}{' '}
                          {t('amicales.table.childrenShort')}
                        </div>
                      </>
                    ) : (
                      <>
                        {formatDate(r.reservation_date || r.check_in)}
                        <div className='text-xs text-gray-500'>
                          {(r.pax || 1)} {t('amicales.table.personsShort')}
                        </div>
                      </>
                    )}
                  </td>
                  <td className='px-4 py-3 text-right font-semibold text-gray-800'>
                    {formatAmount(r.total_amount, r.currency || 'TND')}
                  </td>
                  <td className='px-4 py-3 text-center'>
                    <VoucherStatusBadge voucherStatus={r.voucher_status} />
                  </td>
                  <td className='px-4 py-3'>
                    <AmicaleReservationActions
                      reservation={r}
                      productType={productType}
                      voucherEmail={voucherEmail}
                      amicalClient={amicalClient}
                      onValidated={onRefresh}
                      onRejected={onRefresh}
                      onCancelled={onRefresh}
                      factureByReservation={factureMap}
                      clientId={clientId}
                      balance={balance}
                    />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className='border-t border-gray-100 px-4 py-3 flex justify-end'>
        <Pagination
          current={page}
          pageSize={pageSize}
          total={total}
          showSizeChanger
          pageSizeOptions={[10, 25, 50, 100]}
          onChange={(p, ps) => onPageChange?.(p, ps)}
        />
      </div>
    </div>
  );
};

export default AmicaleReservationsTable;
